<template>
  <button
    class="info-action-btn"
    :class="{ 'is-disabled': disabled, 'is-fixed-width': width !== null }"
    :style="computedStyle"
    :disabled="disabled"
    :title="title"
    @click="$emit('click')"
  >
    <i v-if="icon" :class="iconClass">{{ iconSet === 'material' ? icon : '' }}</i>
    <span>{{ label }}</span>
    <span
      v-if="progress !== null"
      class="info-action-btn__bar"
      :style="{ width: barWidth, background: resolvedBarColor }"
    ></span>
  </button>
</template>

<script>
// Forma compartilhada dos botões de ação da linha do "Marcar como concluído"
// (avaliar, baixar). Existe pra os dois lerem como irmãos do botão "IA" logo
// abaixo — copiar as medidas em cada um garantia que divergissem no primeiro
// ajuste. Puramente apresentacional: não sabe o que a ação faz.
export default {
  name: 'InfoActionButton',
  props: {
    label: { type: String, required: true },
    icon: { type: String, default: null },
    // 'material' usa o glifo como conteúdo; 'fa' usa classe (fa fa-star).
    iconSet: {
      type: String,
      default: 'material',
      validator: v => ['material', 'fa'].includes(v)
    },
    // Cor do texto/ícone. Ausente = cor padrão do tema.
    color: { type: String, default: null },
    borderColor: { type: String, default: null },
    // 0..100 desenha a barra na borda inferior. null = sem barra.
    progress: { type: Number, default: null },
    // Ausente = cor do tema. Nunca hardcode aqui: o produtor define a paleta.
    barColor: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    title: { type: String, default: null },
    // Largura fixa. Existe porque rótulo que muda de estado ("Baixar" → "Na
    // fila" → "42%" → "Offline") faz o botão pular de tamanho a cada transição,
    // e os vizinhos da linha se mexem junto.
    width: { type: [Number, String], default: null }
  },
  computed: {
    iconClass () {
      return this.iconSet === 'fa' ? `fa ${this.icon}` : 'material-icons';
    },
    resolvedBarColor () {
      return this.barColor || this.$theme.primaryColor;
    },
    barWidth () {
      return `${Math.max(0, Math.min(100, this.progress || 0))}%`;
    },
    // A borda/texto brancos do .tablink somem no fundo claro (#f2f3f7) do
    // box-info, então o tema claro precisa dos seus quando não há cor de estado.
    computedStyle () {
      const style = {};
      if (this.color) style.color = this.color;
      if (this.borderColor) style.borderColor = this.borderColor;
      if (!this.color && !this.$theme.darkTheme) {
        style.color = '#333333';
        style.borderColor = 'rgba(0,0,0,0.12)';
      }
      if (this.width !== null) {
        style.width = typeof this.width === 'number' ? `${this.width}px` : this.width;
      }
      return style;
    }
  }
};
</script>

<style scoped lang="scss">
// Mesmas medidas do .tablink (_info.scss): 35px de altura, raio 10, borda
// hairline branca, fundo transparente, Roboto 14.
.info-action-btn {
    flex: 0 0 auto;
    height: 35px;
    padding: 0 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    background: transparent;
    color: #ffffff;
    font-family: Roboto;
    font-size: 14px;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    cursor: pointer;
    // A barra de progresso é absoluta e sangraria os cantos sem isto.
    position: relative;
    overflow: hidden;
    // Sem o nowrap o rótulo quebra em duas linhas quando a linha aperta —
    // a largura disponível a 360px é justa.
    white-space: nowrap;
    margin-right: 0;

    .material-icons {
        font-size: 17px;
        line-height: 1;
    }

    .fa {
        font-size: 15px;
        line-height: 1;
    }

    &:hover:not(.is-disabled) {
        background-color: rgba(255, 255, 255, 0.06);
    }

    // Com largura fixa o padding lateral vira folga desperdiçada — a largura
    // já reserva o espaço e o conteúdo é centralizado.
    &.is-fixed-width {
        padding: 0 6px;
    }

    &.is-disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    // Progresso na borda inferior: num botão largo a barra lê melhor que um
    // anel de 20px, e deixa o número respirar com o "%" e tudo.
    &__bar {
        position: absolute;
        left: 0;
        bottom: 0;
        height: 2px;
        transition: width 0.4s ease;
    }
}
</style>
